/**
 * ALIASES - Role-Based Color Mapping
 *
 * Maps roles to NBX palette references. `primary` and `secondary` point at the mode-aware
 * `--nbx-color-primary-*` / `--nbx-color-secondary-*` palettes, so switching the NBX mode
 * (`<html data-nbx-mode="ssr|lock">`) or overriding those custom properties for a tenant
 * brand re-themes every role without rebuilding the preset:
 * - Rentals (default): primary → Rentals magenta, secondary → Rentsody blue
 * - SSR:               primary → SSR blue,       secondary → SSR neutral grey
 * - Lock (inferred):   primary → Lock orange,    secondary → Rentsody blue
 *
 * Token Hierarchy:
 * Primitives → Aliases (this file) → Semantic → PrimeNG Preset
 */
import { type NbxTokenId, nbxVar } from '../../tokens/generated/nbx-tokens.js';
import {
  guestPurple,
  homeAquamarine,
  lockOrange,
  neutral,
  rentalsMagenta,
  rentsodyBlue,
} from './primitives.js';

const SHADES = [
  '50',
  '100',
  '200',
  '300',
  '400',
  '500',
  '600',
  '700',
  '800',
  '900',
  '950',
] as const;

/** A 50–950 palette of references to one mode-aware NBX colour role. */
function role(name: 'primary' | 'secondary' | 'success' | 'warning' | 'danger') {
  return Object.fromEntries(
    SHADES.map(shade => [shade, nbxVar(`color.${name}.${shade}` as NbxTokenId)])
  ) as Record<(typeof SHADES)[number], string>;
}

/** PRIMARY - Current Sub-brand Hero Color (mode-aware) */
export const primary = role('primary');

/** SECONDARY - Global Core (mode-aware) */
export const secondary = role('secondary');

/** SUCCESS - Green */
export const success = role('success');

/** WARNING - Yellow */
export const warning = role('warning');

/** DANGER - Red */
export const danger = role('danger');

/** INFO - Neutral Blue (same as Rentsody) */
export const info = { ...rentsodyBlue };

/** SURFACE - Neutral (same as Rentsody Blue) */
export const surface = { ...neutral };

/** Lock product palette - Orange */
export const lock = { ...lockOrange };

/** Home product palette - Aquamarine */
export const home = { ...homeAquamarine };

/** Guest product palette - Purple */
export const guest = { ...guestPurple };

/** Rentsody palette - Blue */
export const rentsody = { ...rentsodyBlue };

/** Rentals palette - Magenta */
export const rentals = { ...rentalsMagenta };
